import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { AlertTriangle, BadgeCheck, Ban, Edit3, Eye, Filter, GitMerge, RefreshCw, Save, Search, UserRound, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminApiError,
  AdminStoreCustomer,
  AdminStoreCustomerDetail,
  AdminStoreCustomerPayload,
  AdminStoreUserLocation,
  AdminUser,
  PaginationMeta,
  getAdminStoreCustomer,
  getCurrentAdmin,
  listAdminStoreCustomers,
  logout,
  mergeAdminStoreCustomer,
  updateAdminStoreCustomer,
} from "./api";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

type CustomerForm = {
  Name: string;
  Phone: string;
  Email: string;
  Gender: string;
  Birthday: string;
  AddressCity: string;
  AddressDistrict: string;
  AddressPostalCode: string;
  AddressLine: string;
  PreferredLocationId: string;
  Status: string;
  IsBlacklisted: boolean;
  Note: string;
};

const emptyForm: CustomerForm = {
  Name: "",
  Phone: "",
  Email: "",
  Gender: "",
  Birthday: "",
  AddressCity: "",
  AddressDistrict: "",
  AddressPostalCode: "",
  AddressLine: "",
  PreferredLocationId: "",
  Status: "Active",
  IsBlacklisted: false,
  Note: "",
};

function formatMoney(value?: number | null) {
  return `NT$ ${Number(value || 0).toLocaleString("zh-TW")}`;
}

function text(value: unknown) {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

function valueOf(row: Record<string, unknown>, key: string) {
  return text(row[key]);
}

function customerToForm(customer: AdminStoreCustomerDetail | null): CustomerForm {
  if (!customer) return emptyForm;
  return {
    Name: customer.Name || "",
    Phone: customer.Phone || "",
    Email: customer.Email || "",
    Gender: customer.Gender || "",
    Birthday: customer.Birthday || "",
    AddressCity: customer.AddressCity || "",
    AddressDistrict: customer.AddressDistrict || "",
    AddressPostalCode: customer.AddressPostalCode || "",
    AddressLine: customer.AddressLine || "",
    PreferredLocationId: customer.PreferredLocationId || "",
    Status: customer.Status || "Active",
    IsBlacklisted: Boolean(customer.IsBlacklisted),
    Note: customer.Note || "",
  };
}

function formToPayload(form: CustomerForm): AdminStoreCustomerPayload {
  return {
    Name: form.Name.trim(),
    Phone: form.Phone.trim() || null,
    Email: form.Email.trim() || null,
    Gender: form.Gender.trim() || null,
    Birthday: form.Birthday || null,
    AddressCity: form.AddressCity.trim() || null,
    AddressDistrict: form.AddressDistrict.trim() || null,
    AddressPostalCode: form.AddressPostalCode.trim() || null,
    AddressLine: form.AddressLine.trim() || null,
    PreferredLocationId: form.PreferredLocationId || null,
    Status: form.Status,
    IsBlacklisted: form.IsBlacklisted,
    Note: form.Note.trim() || null,
  };
}

export default function AdminStoreCustomers() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminStoreCustomer[]>([]);
  const [selected, setSelected] = useState<AdminStoreCustomerDetail | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [locations, setLocations] = useState<AdminStoreUserLocation[]>([]);
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("Active");
  const [memberState, setMemberState] = useState("All");
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<CustomerForm>(emptyForm);
  const [mergeFromId, setMergeFromId] = useState("");
  const [mergeReason, setMergeReason] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const statusLabelMap = useMemo(() => ({ Active: "有效客戶", Blacklisted: "黑名單 / 注意", Inactive: "已停用", All: "全部" }), []);
  const memberStateLabelMap = useMemo(() => ({ All: "全部", Linked: "已關聯會員", Unlinked: "未關聯會員" }), []);
  const locationLabelMap = useMemo(() => Object.fromEntries(locations.map((item) => [item.LocationId, item.Name])), [locations]);
  const filterSummary = useMemo(() => {
    const parts = [];
    if (keyword.trim()) parts.push(`關鍵字：${keyword.trim()}`);
    if (status !== "Active") parts.push(`狀態：${statusLabelMap[status as keyof typeof statusLabelMap] || status}`);
    if (memberState !== "All") parts.push(`會員：${memberStateLabelMap[memberState as keyof typeof memberStateLabelMap] || memberState}`);
    if (locationIds.length > 0) {
      const locationText = locationIds.slice(0, 2).map((id) => locationLabelMap[id] || id).join("、");
      parts.push(`門市：${locationText}${locationIds.length > 2 ? ` 等 ${locationIds.length} 間` : ""}`);
    }
    return parts.length ? parts.join(" / ") : "預設：有效客戶";
  }, [keyword, locationIds, locationLabelMap, memberState, memberStateLabelMap, status, statusLabelMap]);

  async function load(filters = { Keyword: keyword, Status: status, MemberState: memberState, LocationIds: locationIds, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminStoreCustomers(filters);
      setItems(data.Customers || []);
      setCounts(data.Counts || {});
      setPagination(data.Pagination || defaultPagination);
      setLocations(data.Locations || []);
      const keepId = selected?.CustomerId;
      const next = keepId ? data.Customers.find((item) => item.CustomerId === keepId) : data.Customers[0];
      if (next) await openDetail(next.CustomerId, false);
      else {
        setSelected(null);
        setForm(emptyForm);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "客戶主檔載入失敗。");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Keyword: "", Status: "Active", MemberState: "All", LocationIds: [], Page: 1, PerPage: defaultPagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openDetail(id: string, switchTab = true) {
    try {
      const data = await getAdminStoreCustomer(id);
      setSelected(data.Customer);
      setForm(customerToForm(data.Customer));
      setEditing(false);
      setMergeFromId("");
      setMergeReason("");
      if (switchTab) setActiveTab("profile");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "客戶詳情讀取失敗。");
    }
  }

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Keyword: keyword.trim(), Status: status, MemberState: memberState, LocationIds: locationIds, Page: 1, PerPage: pagination.PerPage });
    setFiltersOpen(false);
  }

  function resetFilters() {
    setKeyword("");
    setStatus("Active");
    setMemberState("All");
    setLocationIds([]);
    setFiltersOpen(false);
    load({ Keyword: "", Status: "Active", MemberState: "All", LocationIds: [], Page: 1, PerPage: pagination.PerPage });
  }

  async function saveProfile() {
    if (!selected) return;
    setSaving(true);
    setMessage("");
    try {
      const data = await updateAdminStoreCustomer(selected.CustomerId, formToPayload(form));
      setSelected(data.Customer);
      setForm(customerToForm(data.Customer));
      setEditing(false);
      setMessage(data.message || "客戶主檔已更新。");
      await load({ Keyword: keyword.trim(), Status: status, MemberState: memberState, LocationIds: locationIds, Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function mergeCustomer() {
    if (!selected || !mergeFromId) return;
    if (!window.confirm("確定要合併客戶主檔？合併後訂單、服務紀錄、互動與已購商品都會改掛到目前客戶。")) return;
    setSaving(true);
    setMessage("");
    try {
      const data = await mergeAdminStoreCustomer(selected.CustomerId, { FromCustomerId: mergeFromId, Reason: mergeReason });
      setSelected(data.Customer);
      setForm(customerToForm(data.Customer));
      setMergeFromId("");
      setMergeReason("");
      setMessage(data.message || "客戶已合併。");
      await load({ Keyword: keyword.trim(), Status: status, MemberState: memberState, LocationIds: locationIds, Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "合併失敗。");
    } finally {
      setSaving(false);
    }
  }

  function toggleLocation(id: string) {
    setLocationIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  }

  const tabs = useMemo(() => [
    ["profile", "主檔資料"],
    ["timeline", "跨門市紀錄"],
    ["orders", "訂單 / 商品"],
    ["support", "客服 / 合併"],
  ], []);

  return (
    <AdminLayout sectionLabel="門市管理" title="客戶主檔管理" user={currentUser} onLogout={handleLogout}>
      <form className="mb-5 rounded-md border border-[#dfe6e1] bg-white p-3 2xl:p-4" onSubmit={handleSearch}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-extrabold text-[#203d32]"><Filter size={16} />篩選條件</div>
            <p className="mt-1 break-words text-sm font-bold leading-6 text-[#7b877f]">{filterSummary}</p>
          </div>
          <button className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => setFiltersOpen((current) => !current)} type="button">
            {filtersOpen ? "收合篩選" : "展開篩選"}
          </button>
        </div>
        {filtersOpen ? <div className="mt-3 grid gap-3 border-t border-[#edf0ee] pt-3 lg:grid-cols-[minmax(280px,1fr)_160px_160px_auto] lg:items-end">
          <Field label="關鍵字">
            <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="客戶編號、姓名、電話、Email" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </Field>
          <Field label="狀態">
            <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="Active">有效客戶</option>
              <option value="Blacklisted">黑名單 / 注意</option>
              <option value="Inactive">已停用</option>
              <option value="All">全部</option>
            </select>
          </Field>
          <Field label="會員關聯">
            <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={memberState} onChange={(event) => setMemberState(event.target.value)}>
              <option value="All">全部</option>
              <option value="Linked">已關聯會員</option>
              <option value="Unlinked">未關聯會員</option>
            </select>
          </Field>
          <div className="rwd-action-grid">
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32]" type="submit"><Search size={18} />查詢</button>
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={resetFilters} type="button"><RefreshCw size={18} />重設</button>
          </div>
          <div className="lg:col-span-4"><LocationFilter locations={locations} selected={locationIds} onToggle={toggleLocation} onAll={() => setLocationIds(locations.map((item) => item.LocationId))} onClear={() => setLocationIds([])} /></div>
        </div> : null}
      </form>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="全部客戶" value={counts.Total || 0} />
        <SummaryCard label="有效客戶" value={counts.Active || 0} />
        <SummaryCard label="黑名單 / 注意" value={counts.Blacklisted || 0} danger />
        <SummaryCard label="已停用" value={counts.Inactive || 0} />
      </div>

      {message && <div className="mb-4 rounded-md border border-[#d8e0db] bg-white px-4 py-3 text-sm font-bold text-[#315746]">{message}</div>}

      <div className="grid min-w-0 gap-4 2xl:grid-cols-[minmax(0,1fr)_minmax(520px,.72fr)]">
        <section className="min-w-0 rounded-md border border-[#dfe6e1] bg-white">
          <div className="flex items-center justify-between gap-3 border-b border-[#edf0ee] px-4 py-3">
            <h2 className="text-lg font-extrabold text-[#203d32]">客戶列表</h2>
            <span className="text-sm font-bold text-[#7b877f]">{pagination.Total} 筆</span>
          </div>
          <div className="max-h-[68vh] overflow-auto">
            <table className="w-full min-w-[1280px] table-fixed border-separate border-spacing-0 text-left">
              <thead className="sticky top-0 z-40 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
                <tr><th className="w-[260px] whitespace-nowrap px-4 py-3 font-bold">客戶</th><th className="w-[240px] whitespace-nowrap px-4 py-3 font-bold">聯絡方式</th><th className="w-[220px] whitespace-nowrap px-4 py-3 font-bold">會員 / 門市</th><th className="w-[250px] whitespace-nowrap px-4 py-3 font-bold">紀錄摘要</th><th className="w-[190px] whitespace-nowrap px-4 py-3 font-bold">最近活動</th><th className="sticky right-0 z-50 w-[120px] whitespace-nowrap bg-[#f0f3f1] px-4 py-3 text-right font-bold shadow-[-10px_0_18px_rgba(32,61,50,.08)]">操作</th></tr>
              </thead>
              <tbody>
                {loading ? <tr><td className="px-4 py-10 text-center text-[#7b877f]" colSpan={6}>載入中...</td></tr> : items.length === 0 ? <tr><td className="px-4 py-10 text-center text-[#7b877f]" colSpan={6}>目前沒有符合條件的客戶主檔。</td></tr> : items.map((item) => (
                  <tr className={`border-t border-[#edf0ee] align-top hover:bg-[#fbfcfb] ${selected?.CustomerId === item.CustomerId ? "bg-[#f1f8f3]" : "bg-white"}`} key={item.CustomerId}>
                    <td className="px-4 py-4">
                      <div className="flex min-w-0 items-start gap-2">
                        {item.IsBlacklisted ? <Ban className="mt-1 shrink-0 text-[#b42318]" size={18} /> : <UserRound className="mt-1 shrink-0 text-[#315746]" size={18} />}
                        <div className="min-w-0">
                          <p className="break-words text-lg font-extrabold text-[#203d32]">{item.Name}</p>
                          <p className="break-all text-xs font-bold text-[#7b877f]">{item.CustomerCode}</p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {item.MemberId && <Badge label="系統會員" />}
                            {item.IsBlacklisted && <Badge danger label="注意名單" />}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm leading-6 text-[#59665f]"><b className="text-[#203d32]">{item.Phone || "-"}</b><br /><span className="break-all">{item.Email || "-"}</span></td>
                    <td className="px-4 py-4 text-sm leading-6 text-[#59665f]">會員：{item.MemberId ? `#${item.MemberId}` : "未關聯"}<br />常用門市：{item.PreferredLocationName || "-"}</td>
                    <td className="px-4 py-4 text-sm leading-6 text-[#59665f]">訂單 {item.OrderCount} 筆 / {formatMoney(item.TotalOrderAmount)}<br />服務 {item.ServiceRecordCount} 筆 / 互動 {item.InteractionCount} 筆</td>
                    <td className="px-4 py-4 text-sm leading-6 text-[#59665f]">聯絡：{item.LastContactAt || "-"}<br />訂單：{item.LastOrderAt || "-"}<br />服務：{item.LastServiceAt || "-"}</td>
                    <td className="sticky right-0 z-10 bg-white px-4 py-4 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]"><button className="inline-flex h-10 min-w-[82px] items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => openDetail(item.CustomerId)} type="button"><Eye size={16} />查看</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Keyword: keyword.trim(), Status: status, MemberState: memberState, LocationIds: locationIds, Page: page, PerPage: pagination.PerPage })} />
        </section>

        <aside className="min-w-0 rounded-md border border-[#dfe6e1] bg-white">
          {!selected ? (
            <div className="flex min-h-[480px] flex-col items-center justify-center p-6 text-center text-[#59665f]">
              <UserRound className="mb-3 text-[#9aa8a0]" size={38} />
              <p className="font-bold text-[#203d32]">選擇一位客戶查看主檔</p>
              <p className="mt-2 text-sm leading-6">客戶資料、跨門市紀錄、會員關聯、重複合併與注意事項會顯示在這裡。</p>
            </div>
          ) : (
            <div className="min-w-0">
              <div className="border-b border-[#edf0ee] p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-xs font-bold tracking-[.16em] text-[#7b877f]">CUSTOMER MASTER</p>
                    <h2 className="mt-1 break-words text-2xl font-extrabold text-[#203d32]">{selected.Name}</h2>
                    <p className="mt-1 break-all text-sm font-bold text-[#59665f]">{selected.Phone || "-"} / {selected.Email || "-"}</p>
                  </div>
                  <div className="rwd-action-grid sm:flex sm:w-auto">
                    {!editing ? <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => setEditing(true)} type="button"><Edit3 size={16} />編輯</button> : <>
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => { setEditing(false); setForm(customerToForm(selected)); }} type="button"><X size={16} />取消</button>
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-3 font-bold text-white hover:bg-[#203d32]" disabled={saving} onClick={saveProfile} type="button"><Save size={16} />儲存</button>
                    </>}
                  </div>
                </div>
                <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                  {tabs.map(([key, label]) => <button className={`h-9 shrink-0 rounded-md px-3 text-sm font-bold ${activeTab === key ? "bg-[#315746] text-white" : "bg-[#f0f3f1] text-[#315746]"}`} key={key} onClick={() => setActiveTab(key)} type="button">{label}</button>)}
                </div>
              </div>
              <div className="max-h-[68vh] overflow-auto p-4">
                {activeTab === "profile" && <ProfileTab editing={editing} form={form} locations={locations} selected={selected} setForm={setForm} />}
                {activeTab === "timeline" && <TimelineTab selected={selected} />}
                {activeTab === "orders" && <OrdersTab selected={selected} />}
                {activeTab === "support" && <SupportTab mergeCustomer={mergeCustomer} mergeFromId={mergeFromId} mergeReason={mergeReason} saving={saving} selected={selected} setMergeFromId={setMergeFromId} setMergeReason={setMergeReason} />}
              </div>
            </div>
          )}
        </aside>
      </div>
    </AdminLayout>
  );
}

function ProfileTab({ editing, form, locations, selected, setForm }: { editing: boolean; form: CustomerForm; locations: AdminStoreUserLocation[]; selected: AdminStoreCustomerDetail; setForm: (updater: CustomerForm | ((current: CustomerForm) => CustomerForm)) => void }) {
  if (editing) {
    return <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <EditField label="姓名" value={form.Name} onChange={(value) => setForm((current) => ({ ...current, Name: value }))} />
        <EditField label="電話" value={form.Phone} onChange={(value) => setForm((current) => ({ ...current, Phone: value }))} />
        <EditField label="Email" value={form.Email} onChange={(value) => setForm((current) => ({ ...current, Email: value }))} />
        <EditField label="生日" type="date" value={form.Birthday} onChange={(value) => setForm((current) => ({ ...current, Birthday: value }))} />
        <Field label="性別"><select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3" value={form.Gender} onChange={(event) => setForm((current) => ({ ...current, Gender: event.target.value }))}><option value="">未設定</option><option value="Male">男</option><option value="Female">女</option><option value="Other">其他</option></select></Field>
        <Field label="常用門市"><select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3" value={form.PreferredLocationId} onChange={(event) => setForm((current) => ({ ...current, PreferredLocationId: event.target.value }))}><option value="">未設定</option>{locations.map((item) => <option key={item.LocationId} value={item.LocationId}>{item.Name}</option>)}</select></Field>
        <EditField label="縣市" value={form.AddressCity} onChange={(value) => setForm((current) => ({ ...current, AddressCity: value }))} />
        <EditField label="區域" value={form.AddressDistrict} onChange={(value) => setForm((current) => ({ ...current, AddressDistrict: value }))} />
        <EditField label="郵遞區號" value={form.AddressPostalCode} onChange={(value) => setForm((current) => ({ ...current, AddressPostalCode: value }))} />
        <EditField label="地址" value={form.AddressLine} onChange={(value) => setForm((current) => ({ ...current, AddressLine: value }))} />
        <Field label="主檔狀態"><select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3" value={form.Status} onChange={(event) => setForm((current) => ({ ...current, Status: event.target.value }))}><option value="Active">有效</option><option value="Inactive">停用</option></select></Field>
        <label className="flex min-h-11 items-center gap-3 rounded-md border border-[#dfe6e1] px-3 font-bold text-[#203d32]"><input checked={form.IsBlacklisted} className="h-5 w-5 accent-[#315746]" onChange={(event) => setForm((current) => ({ ...current, IsBlacklisted: event.target.checked }))} type="checkbox" />黑名單 / 重要注意事項</label>
      </div>
      <Field label="注意事項 / 內部備註"><textarea className="min-h-28 w-full rounded-md border border-[#cfd8d2] px-3 py-2" value={form.Note} onChange={(event) => setForm((current) => ({ ...current, Note: event.target.value }))} /></Field>
    </div>;
  }
  return <div className="grid gap-3">
    {selected.IsBlacklisted && <div className="flex items-start gap-2 rounded-md border border-[#f4c7c3] bg-[#fff7f6] p-3 font-bold text-[#9f1d1d]"><AlertTriangle className="mt-0.5 shrink-0" size={18} />此客戶已標記為黑名單 / 重要注意名單，門市服務前請先查看備註。</div>}
    <InfoGrid rows={[
      ["客戶編號", selected.CustomerCode], ["會員關聯", selected.MemberId ? `會員 #${selected.MemberId}` : "未關聯會員"], ["姓名", selected.Name], ["電話", selected.Phone], ["Email", selected.Email], ["性別", selected.Gender], ["生日", selected.Birthday], ["常用門市", selected.PreferredLocationName], ["地址", [selected.AddressPostalCode, selected.AddressCity, selected.AddressDistrict, selected.AddressLine].filter(Boolean).join("")], ["來源", selected.SourceType], ["狀態", selected.IsBlacklisted ? "黑名單 / 注意" : selected.Status], ["最近更新", selected.UpdatedAt],
    ]} />
    <TextBlock title="注意事項 / 內部備註" value={selected.Note} />
    <MiniList title="會員身份識別" rows={selected.Identities} render={(row) => <><b>{valueOf(row, "IdentityType")}</b><span className="break-all text-[#59665f]">{valueOf(row, "IdentityValue")}</span></>} />
    <MiniList title="關聯門市" rows={selected.Locations} render={(row) => <><b>{valueOf(row, "LocationName")}</b><span className="text-[#59665f]">{valueOf(row, "RelationType")} / 最近 {valueOf(row, "LastContactAt")}</span></>} />
  </div>;
}

function TimelineTab({ selected }: { selected: AdminStoreCustomerDetail }) {
  return <div className="space-y-3">
    <MiniList title="跨門市活動時間軸" rows={selected.Activities} render={(row) => <><b>{valueOf(row, "Summary")}</b><span className="text-[#59665f]">{valueOf(row, "CreatedAt")} / {valueOf(row, "LocationName")} / {valueOf(row, "StoreUserName")}</span></>} />
    <MiniList title="服務紀錄" rows={selected.ServiceRecords} render={(row) => <><b>{valueOf(row, "VisitDate")} / {valueOf(row, "ServiceType")}</b><span className="text-[#59665f]">{valueOf(row, "LocationName")} / {valueOf(row, "TrialProductName")}</span><span className="line-clamp-2 text-[#59665f]">{valueOf(row, "Summary")}</span></>} />
  </div>;
}

function OrdersTab({ selected }: { selected: AdminStoreCustomerDetail }) {
  return <div className="space-y-3">
    <MiniList title="門市訂單" rows={selected.Orders} render={(row) => <><Link className="font-extrabold text-[#315746] hover:underline" to={`/admin/store-sales-orders?Keyword=${encodeURIComponent(valueOf(row, "OrderNumber"))}`}>{valueOf(row, "OrderNumber")}</Link><span className="text-[#59665f]">{valueOf(row, "LocationName")} / {valueOf(row, "Status")} / {formatMoney(Number(row.TotalAmount || 0))}</span></>} />
    <MiniList title="已購商品 / 配戴設備" rows={selected.OwnedProducts} render={(row) => <><b>{valueOf(row, "ProductName")}</b><span className="text-[#59665f]">SKU {valueOf(row, "Sku")} / 序號 {valueOf(row, "SerialNumber")} / {valueOf(row, "Status")}</span></>} />
  </div>;
}

function SupportTab({ mergeCustomer, mergeFromId, mergeReason, saving, selected, setMergeFromId, setMergeReason }: { mergeCustomer: () => void; mergeFromId: string; mergeReason: string; saving: boolean; selected: AdminStoreCustomerDetail; setMergeFromId: (value: string) => void; setMergeReason: (value: string) => void }) {
  return <div className="space-y-4">
    <MiniList title="客服紀錄" rows={selected.Tickets} render={(row) => <><b>{valueOf(row, "TicketNumber")} / {valueOf(row, "Subject")}</b><span className="text-[#59665f]">{valueOf(row, "Priority")} / {valueOf(row, "Status")} / {valueOf(row, "UpdatedAt")}</span></>} />
    <section className="rounded-md border border-[#dfe6e1] p-3">
      <div className="mb-3 flex items-center gap-2"><GitMerge size={18} className="text-[#315746]" /><h3 className="font-extrabold text-[#203d32]">重複客戶合併</h3></div>
      {selected.DuplicateCandidates.length === 0 ? <p className="text-sm font-bold text-[#7b877f]">目前沒有依電話或 Email 偵測到可能重複的客戶。</p> : <div className="space-y-2">
        <Field label="合併來源客戶"><select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3" value={mergeFromId} onChange={(event) => setMergeFromId(event.target.value)}><option value="">請選擇要併入目前主檔的客戶</option>{selected.DuplicateCandidates.map((item) => <option key={item.CustomerId} value={item.CustomerId}>{item.CustomerCode} / {item.Name} / {item.Phone || item.Email}</option>)}</select></Field>
        <Field label="合併原因"><textarea className="min-h-20 w-full rounded-md border border-[#cfd8d2] px-3 py-2" value={mergeReason} onChange={(event) => setMergeReason(event.target.value)} placeholder="例如：同電話重複建立、門市訂單與會員資料需整併。" /></Field>
        <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32] disabled:opacity-60" disabled={!mergeFromId || saving} onClick={mergeCustomer} type="button"><GitMerge size={18} />確認合併</button>
      </div>}
    </section>
    <MiniList title="合併紀錄" rows={selected.MergeLogs} render={(row) => <><b>#{valueOf(row, "FromCustomerId")} → #{valueOf(row, "ToCustomerId")}</b><span className="text-[#59665f]">{valueOf(row, "CreatedAt")} / {valueOf(row, "MergedByAdminName")} / {valueOf(row, "Reason")}</span></>} />
  </div>;
}

function Field({ children, label }: { children: ReactNode; label: string }) { return <label className="grid min-w-0 gap-1 text-sm font-bold text-[#59665f]"><span>{label}</span>{children}</label>; }
function EditField({ label, onChange, type = "text", value }: { label: string; onChange: (value: string) => void; type?: string; value: string }) { return <Field label={label}><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type={type} value={value} onChange={(event) => onChange(event.target.value)} /></Field>; }
function SummaryCard({ danger = false, label, value }: { danger?: boolean; label: string; value: number }) { return <div className={`rounded-md border p-4 ${danger ? "border-[#f3c8aa] bg-[#fffaf2]" : "border-[#dfe6e1] bg-white"}`}><div className="flex items-center justify-between gap-3"><p className="text-sm font-bold text-[#7b877f]">{label}</p>{danger ? <AlertTriangle className="text-[#b7791f]" size={18} /> : <UserRound className="text-[#315746]" size={18} />}</div><p className="mt-3 text-3xl font-extrabold text-[#203d32]">{value}</p></div>; }
function Badge({ danger = false, label }: { danger?: boolean; label: string }) { return <span className={`inline-flex h-7 items-center rounded-full px-2.5 text-xs font-extrabold ${danger ? "bg-[#fff1f0] text-[#b42318]" : "bg-[#e9f4ec] text-[#245438]"}`}>{label}</span>; }
function InfoGrid({ rows }: { rows: Array<[string, unknown]> }) { return <div className="grid gap-2 sm:grid-cols-2">{rows.map(([label, value]) => <div className="min-w-0 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3" key={label}><p className="text-xs font-bold text-[#7b877f]">{label}</p><p className="mt-1 break-words font-extrabold text-[#203d32]">{text(value)}</p></div>)}</div>; }
function TextBlock({ title, value }: { title: string; value?: string | null }) { return <section className="rounded-md border border-[#dfe6e1] bg-white p-3"><p className="mb-2 font-bold text-[#203d32]">{title}</p><p className="whitespace-pre-wrap break-words text-sm leading-6 text-[#59665f]">{value || "-"}</p></section>; }
function MiniList({ render, rows, title }: { render: (row: Record<string, unknown>) => ReactNode; rows: Array<Record<string, unknown>>; title: string }) { return <section className="rounded-md border border-[#dfe6e1] bg-white"><div className="flex items-center justify-between border-b border-[#edf0ee] px-3 py-2"><h3 className="font-extrabold text-[#203d32]">{title}</h3><span className="text-xs font-bold text-[#7b877f]">{rows.length} 筆</span></div><div className="max-h-[360px] overflow-y-auto p-3">{rows.length === 0 ? <p className="rounded-md border border-dashed border-[#cfd8d2] p-5 text-center text-sm font-bold text-[#7b877f]">目前沒有資料。</p> : <div className="space-y-2">{rows.map((row, index) => <div className="grid gap-1 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3 text-sm" key={index}>{render(row)}</div>)}</div>}</div></section>; }
function LocationFilter({ locations, selected, onToggle, onAll, onClear }: { locations: AdminStoreUserLocation[]; selected: string[]; onToggle: (id: string) => void; onAll: () => void; onClear: () => void }) { return <div className="mt-4 border-t border-[#edf0ee] pt-3"><div className="mb-2 grid gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-between"><label className="inline-flex items-center gap-2 text-sm font-bold text-[#59665f]"><Filter size={15} />門市篩選</label><div className="flex flex-wrap items-center gap-2 text-xs font-bold text-[#315746]"><span className="text-[#7b877f]">已選 {selected.length} 間</span><button className="hover:underline" onClick={onAll} type="button">全選</button><span className="text-[#c9d2cc]">|</span><button className="hover:underline" onClick={onClear} type="button">清除</button></div></div><div className="flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible">{locations.map((location) => { const checked = selected.includes(location.LocationId); return <label className={`flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm font-bold ${checked ? "border-[#9ac8aa] bg-[#f1f8f3] text-[#245438]" : "border-[#d8dedb] bg-white text-[#59665f]"}`} key={location.LocationId}><input checked={checked} className="h-4 w-4 accent-[#315746]" onChange={() => onToggle(location.LocationId)} type="checkbox" />{location.Name}</label>; })}</div></div>; }
